<script context="module">
  // This script runs immediately in the browser, before any component code
  // We no longer need aggressive navigation protection, auth is working properly now

  /* Removed complex window/DOM manipulation from module context */
</script>

<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import {
    user,
    isAuthenticated,
    setSession,
    shouldAllowAutoSignIn,
    refreshUserData,
    lastRefreshed,
  } from '$stores/sessionStore';
  import { selectedStudy, selectedSite } from '$stores/selectionStore';
  import { page } from '$stores/pageStore';
  import { authApi, studies, sites } from '$lib/api';
  import { navigate as goto } from 'svelte-routing';
  const browser = typeof window !== 'undefined';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import ProfileHeader from '$lib/ProfileHeader.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';

  // data is now built up locally in onMount instead of being passed in by SvelteKit's load().
  let data = { userData: null, noRedirect: false, dashboardLoadAttempted: false };

  // Folded from former dashboard/+page.js: auto-redirect based on assigned studies/sites.
  async function handleAutoRedirect(userData) {
    if (!browser || !userData) return;

    const studyIds = userData.studyIds || [];
    const siteIds = userData.siteIds || [];

    if (studyIds.length > 0) {
      try {
        const study = await studies.byProtocol(studyIds[0]);
        selectedStudy.set(study);

        if (siteIds.length > 0) {
          try {
            const siteResponseData = await sites.byIdParam(siteIds[0]);
            // byIdParam takes only siteId; we also need studyGUID. Fall back to params form.
            const site = siteResponseData?.data || siteResponseData;
            if (site) {
              selectedSite.set(site);
              goto('/persons');
              return;
            }
            goto('/sites');
          } catch (siteError) {
            console.error('Error fetching site for auto-redirect:', siteError);
            // Retry with explicit studyGUID through byId params helper.
            try {
              const altResp = await sites.byId({ siteId: siteIds[0], studyGUID: study._id });
              const site = altResp?.data || altResp;
              if (site) {
                selectedSite.set(site);
                goto('/persons');
                return;
              }
            } catch (_) {
              /* fall through */
            }
            goto('/sites');
          }
        } else {
          goto('/sites');
        }
      } catch (error) {
        console.error('Error fetching study for auto-redirect:', error);
        goto('/studies');
      }
    } else {
      goto('/studies');
    }
  }

  // Folded from former dashboard/+page.js load(): try store, URL param, localStorage,
  // then API to populate session data, and trigger auto-redirect.
  async function loadDashboard() {
    if (!browser) return;

    const currentUser = get(user);
    if (currentUser) {
      data = { userData: currentUser, noRedirect: true, dashboardLoadAttempted: true };
      return;
    }

    const url = $page.url;
    const userDataParam = url.searchParams.get('userData');
    if (userDataParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userDataParam));
        setSession(userData);
        localStorage.setItem('lastRefreshed', Date.now().toString());
        window.history.replaceState({}, document.title, window.location.pathname);
        data = { userData, noRedirect: true, dashboardLoadAttempted: true };
        return;
      } catch (e) {
        console.error('Error parsing userData from URL params:', e);
      }
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData && shouldAllowAutoSignIn()) {
          setSession(userData);
          data = { userData, noRedirect: true, dashboardLoadAttempted: true };
          return;
        }
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
      }
    }

    try {
      const userProfile = await authApi.profile();
      const roles = userProfile.roles || ['site'];
      const userData = {
        id: userProfile.sub,
        email: userProfile.email,
        firstName:
          userProfile.firstName ||
          userProfile.given_name ||
          userProfile.name?.split(' ')[0] ||
          '',
        lastName:
          userProfile.lastName ||
          userProfile.family_name ||
          userProfile.name?.split(' ').slice(1).join(' ') ||
          '',
        picture: userProfile.picture,
        customData: {
          roles,
          organizations: userProfile.organizations || [
            {
              organizationId: 'default',
              role: roles[0] || 'admin',
              status: 'active',
              sessionId: userProfile.sid || userProfile.sub
            }
          ],
          primaryOrganizationId: userProfile.primaryOrganizationId || 'default',
          primaryOrganizationName: userProfile.primaryOrganizationName || 'Default',
          primaryOrganizationDisplayName:
            userProfile.primaryOrganizationDisplayName || 'Default Organization'
        },
        sessionId: userProfile.sid || userProfile.sub,
        studyIds: userProfile.studyIds || [],
        siteIds: userProfile.siteIds || [],
        tetrisHighScore: userProfile.tetrisHighScore || 0,
        readOnly: userProfile.readOnly || false,
        blinded: userProfile.blinded || false
      };

      setSession(userData);
      localStorage.setItem('lastRefreshed', Date.now().toString());

      await handleAutoRedirect(userData);

      data = { userData, noRedirect: true, dashboardLoadAttempted: true };
      return;
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }

    data = { userData: null, noRedirect: false, dashboardLoadAttempted: true };
  }

  onMount(async () => {
    await loadDashboard();

    const noRedirect = data?.noRedirect === true;
    if (!data.userData && !noRedirect && browser) {
      // No server-side userData — the SPA needs to authenticate.
      // Send the user to /login, which kicks off the SPA SDK redirect
      // flow (lib/auth.js → loginWithRedirect → Auth0 → callback to /).
      // The legacy direct hop to /api/v1/auth/login was a SvelteKit
      // server-flow that no longer exists in the Spring backend.
      goto('/login');
      return;
    }

    // Keep session store synced with the freshly-loaded userData.
    if (data.userData && browser) {
      const currentUserInStore = $user;
      if (
        !currentUserInStore ||
        currentUserInStore.id !== data.userData.id ||
        currentUserInStore.sessionId !== data.userData.sessionId
      ) {
        setSession(data.userData);
        lastRefreshed.set(Date.now());
        localStorage.setItem('lastRefreshed', Date.now().toString());
      }
    }
  });
</script>

<StudyHeader />

<FeatureInfo
  title="Dashboard"
  description="This page provides an overview of your profile information, latest portal features and usage guidance.
  This page will be updated with more information as we add new features. It can be reached by clicking on your name/email in the top right corner of the portal."
  marginBottom="mb-0"
/>

<ProfileHeader />

<div class="px-1 pt-3">
  <!-- Profile Section with horizontal layout -->
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-3">
    <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">Profile</h2>

    {#if data.userData}
      <div class="flex flex-col md:flex-row gap-6">
        <!-- User Profile Info -->
        <div class="flex items-center md:w-1/3">
          {#if data.userData.picture}
            <img src={data.userData.picture} alt="Profile" class="h-14 w-14 rounded-full mr-4" />
          {:else}
            <div
              class="h-14 w-14 bg-gray-200 dark:bg-gray-600 rounded-full mr-4 flex items-center justify-center text-xl"
            >
              {data.userData.firstName?.[0] || data.userData.email?.[0] || '?'}
            </div>
          {/if}

          <div>
            <h3 class="text-lg font-medium text-gray-500 dark:text-gray-400">
              {data.userData.firstName || ''}
              {data.userData.lastName || ''}
            </h3>
            {#if data.userData.email !== data.userData.firstName}
              <p class="text-gray-500 dark:text-gray-400">
                {data.userData.email || 'No email available'}
              </p>
            {:else}
              <p class="text-gray-500 dark:text-gray-400">&nbsp;</p>
            {/if}
          </div>
        </div>

        <!-- Account Information -->
        <div class="md:w-2/3 md:border-l md:pl-6 md:border-gray-200 md:dark:border-gray-700">
          <div class="flex flex-col lg:flex-row lg:space-x-6 space-y-3 lg:space-y-0">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">User ID</p>
              <p
                class="mt-1 text-sm text-gray-900 dark:text-white truncate"
                title={data.userData.id || 'Not available'}
              >
                {data.userData.id || 'Not available'}
              </p>
            </div>

            <div class="flex-none min-w-0 lg:w-24">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Role</p>
              <p class="mt-1 text-sm text-gray-900 dark:text-white">
                {(() => {
                  const roleDisplayNames = {
                    'admin': 'Administrator',
                    'read-only-b': 'Read Only Blinded',
                    'read-only-u': 'Read Only Unblinded',
                    'site': 'Site User',
                    'study': 'Study User',
                    'support': 'Support User'
                  };
                  const role = data.userData.customData?.roles?.[0];
                  return roleDisplayNames[role] || role || 'Standard user';
                })()}
              </p>
            </div>

            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Organization</p>
              <p class="mt-1 text-sm text-gray-900 dark:text-white">
                {data.userData.customData?.primaryOrganizationDisplayName ||
                  data.userData.customData?.primaryOrganizationName ||
                  'Default Organization'}
              </p>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <p class="text-gray-500 dark:text-gray-400">Loading profile information...</p>
    {/if}
  </div>

  <!-- What's New? Section -->
  {#if "What's New xxx?" == "What's New?"}
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
        Summa Portal
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <!-- Left Column: Navigation Info -->
        <div class="space-y-4 md:pr-4 md:col-span-6">
          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">What's New?</h3>
            <p class="text-gray-600 dark:text-gray-300 mt-1">This build:</p>

            <div class="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-red-700 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  1
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Changelog</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                The <a
                  href="/changelog"
                  class="text-xs px-1 py-1 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500 hover:text-sky-700 dark:hover:text-sky-500 border border-orange-400 dark:border-cyan-500"
                  title="Changelog">Changelog</a
                >
                tracks all changes to the portal by build number.<br />
                Click the build number on the sidebar to view it.
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  2
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Rezolute Data</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                This demo provides access to the Rezolute study. Rezolute has a good balance of
                active participants and features to allow testing of the portal with real data. This
                will allow side by side comparison against SUMMA Site Portal. <b
                  >Everything is read-only to prevent accidental changes.</b
                >
              </p>

              <!-- <div class="flex items-center mb-2">
              <div
                class="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs mr-3"
              >
                3
              </div>
              <h4 class="font-medium text-gray-700 dark:text-gray-300">Feature Information</h4>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
              All pages have a 'Feature Information' section that provides a description of the page
              and its features. It can be switched on and off by clicking 'Show/Hide Feature Info'
              at the bottom of the sidebar menu. Try it now! Once you know how everything works, you
              can turn it off ;).
            </p>

            <div class="flex items-center mb-2">
              <div
                class="w-6 h-6 bg-red-400 rounded-full flex items-center justify-center text-white text-xs mr-3"
              >
                4
              </div>
              <h4 class="font-medium text-gray-700 dark:text-gray-300">Known Issues</h4>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
              Flags for countries and languages are not showing on Windows. They work on Mac
              (Safari, Chrome), on iPad (Safari).
              <br />
              The search fields are incosistent, some start a search on typing, some wait until the user
              hits enter.
            </p> -->
            </div>
          </div>
        </div>

        <!-- Right Column: Feedback -->
        <div class="space-y-4 md:col-span-6">
          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">Feedback</h3>
            <p class="text-gray-600 dark:text-gray-300 mt-1">
              Please provide as much feedback as possible.
            </p>

            <div class="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  1
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">UI/UX</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                How do you like the portal? Is it easy to use? What do you think is great and what
                is not so great? Does it work well on your device/operating system/browser/screen
                size?
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  2
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Functionality</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                What do you think is missing? What do you think is confusing? Please let us know.
                Now is the time to ask for features and improvements for internal use as well as for
                production so we can build a solid product pipeline.
                <br />
                We will focus on requirements for the NN Onwards 11 study but want to make sure Portal
                2.2 is of value to support and data teams as well.
              </p>

              <!-- <div class="flex items-center mb-2">
              <div
                class="w-6 h-6 bg-orange-400 rounded-full flex items-center justify-center text-white text-xs mr-3"
              >
                3
              </div>
              <h4 class="font-medium text-gray-700 dark:text-gray-300">Perfomance</h4>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
              Although this is a demo, we want to make sure the portal is performant. We have some
              known areas that need optimization - mostly query performance, but please point out
              any performance issues. As this demo runs on a production database with 1.5+ years of
              real data, we should be able to gauge real world performance.
            </p> -->
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if "Did you know?" == "Did you know?"}
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">Did you know?</h2>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <!-- Left Column: Navigation Info -->
        <div class="space-y-4 md:pr-4 md:col-span-6">
          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">
              Navigation Hierarchy
            </h3>
            <p class="text-gray-600 dark:text-gray-300 mt-1">
              The portal follows a hierarchical structure:
            </p>

            <div class="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  1
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Study</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                The top level of organization. Each study contains multiple sites.
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  2
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Site</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                A location or facility where study participants are enrolled and data is collected.
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-indigo-400 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  3
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Participant</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9">
                Individual subjects enrolled in the study at a specific site.
              </p>
            </div>
          </div>

          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">How to Navigate</h3>
            <ul class="mt-2 space-y-2 text-gray-600 dark:text-gray-400">
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span>Use the sidebar menu to access different sections of the portal.</span>
              </li>
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span
                  >Select a study from the Studies list to view all sites associated with that
                  study.</span
                >
              </li>
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span>Click on a site to see all participants enrolled at that location.</span>
              </li>
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span>Select a participant to view or enter their specific data.</span>
              </li>
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span
                  >Use the breadcrumb navigation at the top of each page to move back up the
                  hierarchy.</span
                >
              </li>
              <li class="flex items-start">
                <span class="text-indigo-600 mr-2">•</span>
                <span
                  >Use the universal search bar to find participants using their Id or GUID.</span
                >
              </li>
            </ul>
          </div>
        </div>

        <!-- Right Column: Reports Info -->
        <div class="space-y-4 md:col-span-6">
          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">Accessing Reports</h3>
            <p class="text-gray-600 dark:text-gray-300 mt-1">
              Reports are available at each level of the hierarchy and are automatically filtered
              based on your current context:
            </p>

            <div class="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  1
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Study Level Reports</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                When viewing a study, reports contain data aggregated across all sites and
                participants within the study. This is only recommended for small studies.
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  2
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">Site Level Reports</h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9 mb-3">
                When viewing a site, reports are filtered to show data only from that specific site.
              </p>

              <div class="flex items-center mb-2">
                <div
                  class="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center text-white text-xs mr-3"
                >
                  3
                </div>
                <h4 class="font-medium text-gray-700 dark:text-gray-300">
                  Participant Level Reports
                </h4>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 ml-9">
                When a participant is selected, reports display data specific to that individual
                participant only.
              </p>
            </div>
          </div>

          <div>
            <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300">Report Features</h3>
            <ul class="mt-2 space-y-2 text-gray-600 dark:text-gray-400">
              <li class="flex items-start">
                <span class="text-green-600 mr-2">•</span>
                <span>Access reports from the sidebar within each hierarchy level.</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-600 mr-2">•</span>
                <span>Filter reports by date range, data type, or other criteria.</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-600 mr-2">•</span>
                <span>Export to native Excel is available for all reports.</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-600 mr-2">•</span>
                <span>Save custom report configurations for future use. (coming soon)</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-600 mr-2">•</span>
                <span>Schedule automated report generation and delivery. (coming later)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
